import { X } from "lucide-react";
import {
  AddCitationModal,
  type UploadCitationResponse,
} from "./AddCitationModal";
import { EditCitationSummaryModal } from "./EditCitationSummaryModal";
import { Button } from "../../../../components/ui/button";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import {
  type AuditSession,
  type SourceRow,
} from "../../../../models/audit/auditSession";
import { ProcedureSessionTableShell } from "../../../../models/audit/procedureSessionTableShell";
import { type AttributeTestColumnResult } from "../../../../models/audit/attributeTestColumn";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAudit } from "../../../../contexts/auditContext.tsx";
import { useHookCache } from "../../../../lib/hookCache";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../components/ui/sheet";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../../components/ui/tabs";
import {
  Citation,
  extractTimestampFromCitationString,
} from "../../../../models/audit/citation";
import { fetchFastApi } from "../../../../api/fastapi";
import { LinkingData } from "../../../extraction/viewExtraction/PdfViewer";
import { useColumnActionLogs } from "../../../../hooks/useColumnActionLogs.ts";
import { useAttributeTestData } from "../../../../hooks/useAttributeTestData";
import { useCitationSelection } from "../../../../hooks/useCitationSelection";
import { useCitationActions } from "../../../../hooks/useCitationActions";
import { OverviewTabContent } from "./attributeTestAnalysis/OverviewTabContent";
import { WalkthroughTabContent } from "./attributeTestAnalysis/WalkthroughTabContent";
import { AttributeTestAnalysisProvider } from "./attributeTestAnalysis/AttributeTestAnalysisContext";
import { useHideSourceDocCitations } from "../../../../hooks/useHideSourceDocCitations";
import {
  filterSourceDocCitationsFromSession,
  hasScreenshotCitations,
} from "../../../../lib/citationFilters";
import { Switch } from "../../../../components/ui/switch";
import { VideoCitationCreatorModal } from "./attributeTestAnalysis/VideoCitationCreatorModal";
import { type VideoDocument } from "../../../../models/extraction";
import { useAuth } from "../../../../contexts/authContext";
import { shouldEnableMidshipFeatures } from "../../../../lib/workspaceUtils";
import { EventName, trackEvent } from "../../../../lib/metrics";

// Inserts `newCitation` into the matching group on the step. Used to build the
// optimistic step shape for both source-row and attribute-test detail caches.
// Returns the step unchanged when there's no group id or the id doesn't match.
const addCitationToGroup = <
  S extends { citationGroups?: Array<{ id: string; citations: Citation[] }> },
>(
  step: S,
  citationGroupId: string | null,
  newCitation: Citation,
): S => {
  if (!citationGroupId) return step;
  return {
    ...step,
    citationGroups: (step.citationGroups ?? []).map((group) =>
      group.id === citationGroupId
        ? { ...group, citations: [...group.citations, newCitation] }
        : group,
    ),
  };
};

interface AttributeTestAnalysisProps {
  auditSession: AuditSession;
  checkResultId?: string;
  sourceRowId?: string;
  evalColumnId?: string;
  onClose: () => void;
  handleCheckSelection: ({
    sourceRowId,
    documentId,
    checkResultId,
    supportColumnId,
    value,
  }: {
    sourceRowId?: string;
    attributeId?: string;
    documentId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
  }) => void;
}

export const AttributeTestAnalysis = ({
  auditSession,
  checkResultId,
  sourceRowId,
  onClose,
}: AttributeTestAnalysisProps) => {
  // Phase 3: optimistic writes target the per-procedure cache entry keyed
  // by `auditSession.id`. The heavy `controlSession` tree is read only for
  // envelope-shaped fields (status); per-procedure data flows through the
  // `useProcedureSession`-backed cache.
  const { controlSession, updateCurrentDocument } = useAudit();
  const { setEntry } = useHookCache();
  const { workspace, user } = useAuth();
  const enableAddCitation = shouldEnableMidshipFeatures(
    workspace?.id,
    user?.email,
  );
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showAddCitationModal, setShowAddCitationModal] = useState(false);
  const [showEditCitationModal, setShowEditCitationModal] = useState(false);
  const [citationToEdit, setCitationToEdit] = useState<Citation | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "walkthrough">(
    "overview",
  );
  const [hideSourceDocCitations, setHideSourceDocCitations] =
    useHideSourceDocCitations();

  // Add Citation modal state
  const [addCitationStep, setAddCitationStep] = useState<"closed" | "video">(
    "closed",
  );
  const [selectedDocForCitation, setSelectedDocForCitation] = useState<{
    document: AuditSession["documents"][number];
    signedUrl: string;
    videoDocument?: VideoDocument;
    reperformanceStepId: string | null;
  } | null>(null);

  const {
    attributeTestResultId,
    sourceRow,
    attributeTestResult,
    isLoading,
    column,
    columnName,
    allCitations,
    filteredCitations,
    citationDocuments,
    displayStatus,
  } = useAttributeTestData({
    auditSession,
    checkResultId,
    sourceRowId,
    controlSession,
  });

  // Apply excel filter on top of key evidence filter for the carousel/navigation
  const keyEvidenceCitations = useMemo(() => {
    return filterSourceDocCitationsFromSession(
      filteredCitations,
      auditSession.documents,
      hideSourceDocCitations,
      allCitations,
    );
  }, [
    filteredCitations,
    allCitations,
    auditSession.documents,
    hideSourceDocCitations,
  ]);

  // Filter citation documents based on hideSourceDocCitations setting
  const filteredCitationDocuments = useMemo(() => {
    if (!hideSourceDocCitations) return citationDocuments;

    return citationDocuments
      .map((docEntry) => ({
        ...docEntry,
        citations: filterSourceDocCitationsFromSession(
          docEntry.citations,
          auditSession.documents,
          hideSourceDocCitations,
          allCitations,
        ),
      }))
      .filter((docEntry) => docEntry.citations.length > 0);
  }, [
    citationDocuments,
    allCitations,
    auditSession.documents,
    hideSourceDocCitations,
  ]);

  // Filter reperformance step citations based on hideSourceDocCitations setting
  const filteredReperformanceSteps = useMemo(() => {
    if (!attributeTestResult) return [];
    if (!hideSourceDocCitations) return attributeTestResult.reperformanceSteps;

    return attributeTestResult.reperformanceSteps.map((step) => ({
      ...step,
      citations: filterSourceDocCitationsFromSession(
        step.citations,
        auditSession.documents,
        hideSourceDocCitations,
        allCitations,
      ),
      citationGroups: (step.citationGroups ?? []).map((group) => ({
        ...group,
        citations: filterSourceDocCitationsFromSession(
          group.citations,
          auditSession.documents,
          hideSourceDocCitations,
          allCitations,
        ),
      })),
    }));
  }, [
    attributeTestResult,
    allCitations,
    auditSession.documents,
    hideSourceDocCitations,
  ]);

  const hasScreenshots = useMemo(() => {
    if (allCitations.length === 0) return false;
    return hasScreenshotCitations(allCitations, auditSession.documents);
  }, [allCitations, auditSession.documents]);

  const {
    logs: activityLogs,
    loading: logsLoading,
    fetchLogs: refetchActivityLogs,
  } = useColumnActionLogs({
    attributeTestResultId,
    fetchOnLoad: true,
  });

  const onCitationSelectRef = useRef<(citation: Citation) => void>(() => {});

  // `auditSession` is a procedure session; work-step sessions have no control session.
  const procedureSessionId = auditSession.id;
  const controlSessionId = auditSession.controlSessionId;
  const trackWalkthroughStep = useCallback(
    (citation: Citation, navigationMethod: "click" | "keyboard") => {
      trackEvent(EventName.ViewEvidenceWalkthrough, {
        ...(controlSessionId ? { controlSessionId } : {}),
        procedureSessionId,
        citationId: citation.id,
        navigationMethod,
      });
    },
    [controlSessionId, procedureSessionId],
  );

  // `updateDocumentForCitation` needs `navigation`, so it is wired in through
  // a ref once defined below.
  const { navigation, selectCitation } = useCitationSelection({
    citations: keyEvidenceCitations,
    displayCitation: (citation) => onCitationSelectRef.current(citation),
    // The hook reports only a press that moved the selection. The listener
    // stays mounted on the Overview tab too; only steps taken while the
    // walkthrough is showing count as walkthrough views.
    onKeyboardStep: (citation) => {
      if (activeTab === "walkthrough") {
        trackWalkthroughStep(citation, "keyboard");
      }
    },
  });

  // The bounding box names the citation on screen only while the walkthrough is
  // mounted; from the Overview tab it may hold one the user never saw.
  const displayedCitationId =
    activeTab === "walkthrough"
      ? navigation.currentBoundingBox?.citationId
      : undefined;
  const reportClickedCitation = useCallback(
    (citation: Citation) => {
      if (citation.id !== displayedCitationId) {
        trackWalkthroughStep(citation, "click");
      }
    },
    [displayedCitationId, trackWalkthroughStep],
  );

  const updateDocumentForCitation = useCallback(
    (citation: Citation) => {
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === citation.documentId,
      );

      if (sourceDocument) {
        navigation.setCurrentSelectionValue(citation.shortSummary || undefined);

        // Extract timestamp from citation for video support
        const encodedString = citation.encodedCitationString;
        const parsedTimestamp = extractTimestampFromCitationString(
          encodedString || "",
        );
        const timestampSeconds = citation.timestampSeconds ?? parsedTimestamp;

        navigation.setCurrentBoundingBox({
          boundingBox: citation.boundingBox ?? undefined,
          pageNumber: citation.pageNumber ?? undefined,
          pageWidth: citation.pageWidth ?? undefined,
          pageHeight: citation.pageHeight ?? undefined,
          elementId: citation.encodedCitationString ?? undefined,
          citationId: citation.id ?? undefined,
          timestampSeconds,
        });

        navigation.setFocusedStepId(citation.reperformanceStepId || undefined);

        updateCurrentDocument({
          id: sourceDocument.id,
          documentId: sourceDocument.documentId,
          fileName: sourceDocument.name || undefined,
          fileType: sourceDocument.fetchableDocument?.fileType,
          fetchableDocument: sourceDocument.fetchableDocument || undefined,
          isMissing: false,
          type: sourceDocument.fetchableDocument?.fileType,
          procedureSessionId: auditSession.id,
        });
        setShowDocumentViewer(true);
      } else {
        // The citation's document is not in `auditSession.documents` (e.g. a
        // generated artifact such as the `write_excel_formula` compiled-workbook
        // screenshot). Render it by id via the presigned-URL endpoint's
        // workspace-wide fallback instead of no-op'ing. Navigation/bounding-box
        // state is read from the citation itself, not from a source document.
        navigation.setCurrentSelectionValue(citation.shortSummary || undefined);

        // Extract timestamp from citation for video support
        const encodedString = citation.encodedCitationString;
        const parsedTimestamp = extractTimestampFromCitationString(
          encodedString || "",
        );
        const timestampSeconds = citation.timestampSeconds ?? parsedTimestamp;

        navigation.setCurrentBoundingBox({
          boundingBox: citation.boundingBox ?? undefined,
          pageNumber: citation.pageNumber ?? undefined,
          pageWidth: citation.pageWidth ?? undefined,
          pageHeight: citation.pageHeight ?? undefined,
          elementId: citation.encodedCitationString ?? undefined,
          citationId: citation.id ?? undefined,
          timestampSeconds,
        });

        navigation.setFocusedStepId(citation.reperformanceStepId || undefined);

        updateCurrentDocument({
          id: citation.documentId,
          documentId: citation.documentId,
          procedureSessionId: auditSession.id,
        });
        setShowDocumentViewer(true);
      }
    },
    [
      auditSession.id,
      auditSession.documents,
      updateCurrentDocument,
      navigation,
    ],
  );

  useEffect(() => {
    onCitationSelectRef.current = updateDocumentForCitation;
  }, [updateDocumentForCitation]);

  const handleCitationClick = useCallback(
    (citation: Citation) => {
      reportClickedCitation(citation);
      selectCitation(citation);
    },
    [reportClickedCitation, selectCitation],
  );

  const handleEvidenceIndexChange = useCallback(
    (index: number) => {
      const citation = keyEvidenceCitations[index];
      // Selecting through the citation also displays it when the index is
      // unchanged, which the hook's index effect alone would not do.
      if (citation) {
        handleCitationClick(citation);
        return;
      }
      navigation.setActiveEvidenceIndex(index);
    },
    [keyEvidenceCitations, navigation, handleCitationClick],
  );

  const actions = useCitationActions({
    auditSession,
    sourceRowId,
    checkResultId,
    attributeTestResult,
    allCitations,
    filteredCitations: keyEvidenceCitations,
    activeEvidenceIndex: navigation.activeEvidenceIndex,
    currentBoundingBox: navigation.currentBoundingBox,
    setCurrentBoundingBox: navigation.setCurrentBoundingBox,
    setActiveEvidenceIndex: navigation.setActiveEvidenceIndex,
  });

  const handleDocumentClick = useCallback(
    (documentEntry: {
      document: AuditSession["documents"][number];
      citations: Citation[];
    }) => {
      const firstCitation = documentEntry.citations[0];
      if (firstCitation) {
        handleCitationClick(firstCitation);
        setActiveTab("walkthrough");
      }
    },
    [handleCitationClick],
  );

  const handleEditCitationSummary = useCallback((citation: Citation) => {
    setCitationToEdit(citation);
    setShowEditCitationModal(true);
  }, []);

  const handleCreateCitation = useCallback(
    async (data: {
      summary: string;
      reperformanceStepId: string | null;
      linkingData: LinkingData;
      documentId: string;
      pageWidth?: number;
      pageHeight?: number;
      isKey?: boolean;
      citationGroupId?: string | null;
    }) => {
      if (
        !sourceRowId ||
        !attributeTestResult?.id ||
        !data.reperformanceStepId
      ) {
        throw new Error("Missing required data for citation creation");
      }

      const newCitationId = crypto.randomUUID();
      const now = new Date().toISOString();
      const citationGroupId = data.citationGroupId ?? null;

      const newCitation = {
        id: newCitationId,
        documentId: data.documentId,
        encodedCitationString: `pdf-manual#${data.documentId}`,
        shortSummary: data.summary,
        boundingBox: data.linkingData.boundingBox,
        pageNumber: data.linkingData.pageNumber,
        pageWidth: data.pageWidth ?? null,
        pageHeight: data.pageHeight ?? null,
        citationOrder: (allCitations.length || 0) + 1,
        reperformanceStepId: data.reperformanceStepId,
        citationGroupId,
        isKey: data.isKey ?? true,
        createdAt: now,
        updatedAt: now,
      };

      // The ReperformanceStepsPanel renders citations from step.citationGroups
      // when any exist, so we write the optimistic citation into the matching
      // group only. The carousel reads via useAttributeTestData.allCitations,
      // which iterates groups first, so the new citation shows up there too.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => ({
            ...prev,
            attributeTestResults: prev.attributeTestResults?.map(
              (attrResult) =>
                attrResult.id === attributeTestResult.id
                  ? {
                      ...attrResult,
                      reperformanceSteps: attrResult.reperformanceSteps.map(
                        (step) =>
                          step.id === data.reperformanceStepId
                            ? addCitationToGroup(
                                step,
                                citationGroupId,
                                newCitation,
                              )
                            : step,
                      ),
                    }
                  : attrResult,
            ),
          }),
        );
      }
      // Mirror into the per-attribute-test detail cache used by the side panel.
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          reperformanceSteps: prev.reperformanceSteps.map((step) =>
            step.id === data.reperformanceStepId
              ? addCitationToGroup(step, citationGroupId, newCitation)
              : step,
          ),
        }),
      );

      // POST to the citations endpoint
      await fetchFastApi({
        method: "POST",
        path: `/citations`,
        body: {
          citation: {
            id: newCitation.id,
            document_id: newCitation.documentId,
            encoded_citation_string: newCitation.encodedCitationString,
            short_summary: newCitation.shortSummary,
            bounding_box: newCitation.boundingBox,
            page_number: newCitation.pageNumber,
            page_width: newCitation.pageWidth,
            page_height: newCitation.pageHeight,
            citation_order: newCitation.citationOrder,
            is_key: newCitation.isKey,
            reperformance_step_id: data.reperformanceStepId,
            citation_group_id: citationGroupId,
            created_at: newCitation.createdAt,
            updated_at: newCitation.updatedAt,
          },
        },
      });
    },
    [
      sourceRowId,
      attributeTestResult?.id,
      allCitations.length,
      auditSession.id,
      setEntry,
    ],
  );

  // Handle upload citation created - update local state with new document and citation
  const handleUploadCitationCreated = useCallback(
    (response: UploadCitationResponse) => {
      if (!attributeTestResult?.id || !response.citation.reperformanceStepId) {
        return;
      }

      const { citation, document } = response;

      const newCitation = {
        id: citation.id,
        documentId: citation.documentId,
        encodedCitationString: citation.encodedCitationString,
        shortSummary: citation.shortSummary,
        boundingBox: citation.boundingBox,
        pageNumber: citation.pageNumber,
        pageWidth: citation.pageWidth,
        pageHeight: citation.pageHeight,
        citationOrder: citation.citationOrder,
        reperformanceStepId: citation.reperformanceStepId,
        citationGroupId: citation.citationGroupId,
        isKey: citation.isKey,
        createdAt: citation.createdAt,
        updatedAt: citation.updatedAt,
      };
      const citationGroupId = citation.citationGroupId;

      // Build the document object for local state
      // Use sessionDocument.id for the session-document ID (used by fetchPresignedUrl lookup)
      // Fall back to document.id for backwards compatibility
      const newDocument = {
        id: response.sessionDocument?.id ?? document.id,
        documentId: document.id,
        name: document.fileName,
        fetchableDocument: {
          id: document.id,
          fileName: document.fileName,
          fileType: document.fileType,
          fileUrl: document.fileUrl,
          fileContentHash: document.fileContentHash,
          documentAssignments: null,
          extra: document.extra,
          parentDocumentId: document.parentDocumentId,
          childType: null,
          state: "ready" as const,
          isParsed: document.isParsed,
          createdAt: document.createdAt,
          updatedAt: document.updatedAt,
        },
        creatorType: "user" as const,
        state: "approved" as const,
        extraction: null,
        extractions: [],
        // A file uploaded as a citation is not an analytics input until the
        // auditor marks it as one.
        isAnalyticsInput: false,
      };

      // Write the optimistic citation into the matching citation_group on the
      // step. The panel renders from groups when any exist; the carousel reads
      // via useAttributeTestData.allCitations which iterates groups too.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => ({
            ...prev,
            attributeTestResults: prev.attributeTestResults?.map(
              (attrResult) =>
                attrResult.id === attributeTestResult.id
                  ? {
                      ...attrResult,
                      reperformanceSteps: attrResult.reperformanceSteps.map(
                        (step) =>
                          step.id === citation.reperformanceStepId
                            ? addCitationToGroup(
                                step,
                                citationGroupId,
                                newCitation,
                              )
                            : step,
                      ),
                    }
                  : attrResult,
            ),
          }),
        );
      }
      setEntry<AttributeTestColumnResult>(
        [
          "procedure-session",
          auditSession.id,
          "attribute-test-result",
          attributeTestResult.id ?? "",
        ],
        (prev) => ({
          ...prev,
          reperformanceSteps: prev.reperformanceSteps.map((step) =>
            step.id === citation.reperformanceStepId
              ? addCitationToGroup(step, citationGroupId, newCitation)
              : step,
          ),
        }),
      );
      // Phase 3: the new document is added to the table-shell cache (the
      // Phase 3 primary per-procedure cache) so the synthesised session
      // in `ProcedureSessionWorkPaper` picks it up. The per-row citation
      // mutation is already applied above.
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSession.id, "table-shell"],
        (prev) => ({
          ...prev,
          documents: [...prev.documents, { ...newDocument, sampleNumbers: [] }],
        }),
      );
    },
    [auditSession.id, sourceRowId, attributeTestResult?.id, setEntry],
  );

  // Add Citation flow handlers
  const handleAddCitationClick = useCallback(() => {
    setShowAddCitationModal(true);
  }, []);

  // Handle video document selection (from AddCitationModal search tab)
  const handleVideoDocumentSelect = useCallback(
    (
      document: AuditSession["documents"][number],
      signedUrl: string,
      reperformanceStepId: string | null,
      videoDocument?: VideoDocument,
    ) => {
      setShowAddCitationModal(false);
      setSelectedDocForCitation({
        document,
        signedUrl,
        videoDocument,
        reperformanceStepId,
      });
      setAddCitationStep("video");
    },
    [],
  );

  const handleVideoCitationCreate = useCallback(
    async (timestamp: number, description?: string) => {
      if (!selectedDocForCitation) return;

      await actions.handleCreateCitation({
        documentId: selectedDocForCitation.document.documentId,
        timestampSeconds: timestamp,
        shortSummary: description,
        reperformanceStepId:
          selectedDocForCitation.reperformanceStepId ?? undefined,
      });

      // Reset state and close modals
      setSelectedDocForCitation(null);
      setAddCitationStep("closed");
    },
    [selectedDocForCitation, actions],
  );

  useEffect(() => {
    setShowDocumentViewer(false);
  }, [checkResultId]);

  useEffect(() => {
    if (activeTab === "walkthrough" && allCitations.length > 0) {
      const citationsToUse =
        keyEvidenceCitations.length > 0 ? keyEvidenceCitations : allCitations;
      if (citationsToUse.length > 0 && !navigation.currentBoundingBox) {
        // Automatic selection on opening the tab, not a user step: untracked.
        selectCitation(citationsToUse[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (!sourceRow || !attributeTestResult || !column) {
    // Workpaper-lazy-load: while either `useSourceRow` or
    // `useAttributeTestResultDetail` is in flight, keep the Sheet open and
    // show a spinner instead of the "No attribute test details found."
    // terminal state. The Sheet wrapper mirrors the populated branch below
    // so the panel chrome stays put.
    return (
      <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="w-full p-0 sm:max-w-[720px]"
        >
          <div className="h-screen flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <SheetHeader>
                <SheetTitle>Attribute Test Result</SheetTitle>
              </SheetHeader>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <p className="text-sm text-stone-600">
                  No attribute test details found.
                </p>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const isConclusive = attributeTestResult.isConclusive;
  const outcome =
    isConclusive && attributeTestResult.outcome
      ? attributeTestResult.outcome
      : null;

  return (
    <AttributeTestAnalysisProvider refetchActivityLogs={refetchActivityLogs}>
      <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className={`w-full p-0 ${
            activeTab === "walkthrough"
              ? "sm:w-[85vw] sm:max-w-none"
              : "sm:max-w-[720px]"
          }`}
        >
          <div className="h-screen flex flex-row overflow-hidden">
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                <div className="flex items-center gap-3">
                  <img src="/logo_orange_bg.svg" alt="logo" className="w-6" />
                  <div className="flex flex-col">
                    <SheetHeader>
                      <SheetTitle>Attribute Test Result</SheetTitle>
                    </SheetHeader>
                    <div className="text-xs text-font-secondary">
                      {(sourceRow?.sampleNumber
                        ? `Sample ${sourceRow.sampleNumber}`
                        : "Sample") + (columnName ? ` · ${columnName}` : "")}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col">
                <div className="px-6 py-3">
                  <Tabs
                    value={activeTab}
                    onValueChange={(value) =>
                      setActiveTab(value as "overview" | "walkthrough")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <TabsList>
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="walkthrough">
                          Evidence Walkthrough
                        </TabsTrigger>
                      </TabsList>
                      {hasScreenshots && (
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="screenshot-toggle-attr"
                            className="text-sm text-stone-600 cursor-pointer"
                          >
                            Show screenshots only
                          </label>
                          <Switch
                            id="screenshot-toggle-attr"
                            checked={hideSourceDocCitations}
                            onCheckedChange={setHideSourceDocCitations}
                          />
                        </div>
                      )}
                    </div>

                    <TabsContent value="overview">
                      <OverviewTabContent
                        columnName={columnName}
                        isConclusive={isConclusive}
                        outcome={outcome}
                        displayStatus={displayStatus}
                        attributeTestResult={attributeTestResult}
                        citationDocuments={filteredCitationDocuments}
                        activityLogs={activityLogs}
                        logsLoading={logsLoading}
                        sourceRowId={sourceRowId}
                        onUpdate={actions.handleUpdateField}
                        onDocumentClick={handleDocumentClick}
                        disabled={controlSession?.status === "complete"}
                      />
                    </TabsContent>

                    <TabsContent
                      value="walkthrough"
                      className="h-[calc(100vh-160px)]"
                    >
                      <WalkthroughTabContent
                        reperformanceSteps={filteredReperformanceSteps}
                        filteredCitations={keyEvidenceCitations}
                        activeEvidenceIndex={navigation.activeEvidenceIndex}
                        focusedStepId={navigation.focusedStepId}
                        currentBoundingBox={navigation.currentBoundingBox}
                        viewerBoundingBoxes={actions.viewerBoundingBoxes}
                        selectedBoundingBoxId={actions.selectedBoundingBoxId}
                        selectedBoundingBoxIndex={
                          actions.selectedBoundingBoxIndex
                        }
                        currentSelectionValue={navigation.currentSelectionValue}
                        showDocumentViewer={showDocumentViewer}
                        allCitations={allCitations}
                        onStepToggle={navigation.setFocusedStepId}
                        onCitationClick={handleCitationClick}
                        onAutoSelectCitation={selectCitation}
                        onEvidenceIndexChange={handleEvidenceIndexChange}
                        onLinkingComplete={actions.handleLinkingComplete}
                        onDelete={actions.handleRemoveBoundingBox}
                        onSelect={({ id }) => {
                          if (!id) return;
                          const clicked = allCitations.find((c) => c.id === id);
                          if (!clicked) return;
                          reportClickedCitation(clicked);
                          // Extract timestamp for video support
                          const parsedTimestamp =
                            extractTimestampFromCitationString(
                              clicked.encodedCitationString || "",
                            );
                          const clickedTimestamp =
                            clicked.timestampSeconds ?? parsedTimestamp;
                          navigation.setCurrentBoundingBox({
                            boundingBox: clicked.boundingBox || undefined,
                            pageNumber: clicked.pageNumber || undefined,
                            pageWidth: clicked.pageWidth || undefined,
                            pageHeight: clicked.pageHeight || undefined,
                            elementId:
                              clicked.encodedCitationString || undefined,
                            citationId: clicked.id,
                            timestampSeconds: clickedTimestamp,
                          });
                          navigation.setCurrentSelectionValue(
                            clicked.shortSummary || undefined,
                          );
                          navigation.setFocusedStepId(
                            clicked.reperformanceStepId || undefined,
                          );
                          const citIdx = keyEvidenceCitations.findIndex(
                            (c) => c.id === clicked.id,
                          );
                          if (citIdx >= 0) {
                            navigation.setActiveEvidenceIndex(citIdx);
                          }
                        }}
                        onToggleKeyCitation={actions.handleToggleKeyCitation}
                        onAddCitation={
                          controlSession?.status === "complete"
                            ? undefined
                            : enableAddCitation
                              ? handleAddCitationClick
                              : () => setShowAddCitationModal(true)
                        }
                        onEditCitationSummary={
                          controlSession?.status === "complete"
                            ? undefined
                            : handleEditCitationSummary
                        }
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AddCitationModal
        open={showAddCitationModal}
        onOpenChange={setShowAddCitationModal}
        auditSession={auditSession}
        isAttributeTestColumn={true}
        reperformanceSteps={attributeTestResult.reperformanceSteps}
        onCreateCitation={handleCreateCitation}
        onUploadCitationCreated={handleUploadCitationCreated}
        onVideoDocumentSelect={handleVideoDocumentSelect}
      />

      <EditCitationSummaryModal
        open={showEditCitationModal}
        onOpenChange={setShowEditCitationModal}
        citation={citationToEdit}
        onSave={actions.handleUpdateCitationSummary}
        onDelete={
          controlSession?.status === "complete"
            ? undefined
            : async (citationId) => {
                const index = allCitations.findIndex(
                  (c) => c.id === citationId,
                );
                await actions.handleDeleteCitation({
                  id: citationId,
                  index: index >= 0 ? index : 0,
                });
              }
        }
      />

      {/* Video Citation Creator Modal */}
      {selectedDocForCitation && (
        <VideoCitationCreatorModal
          open={addCitationStep === "video"}
          onOpenChange={(open) => {
            if (!open) {
              setAddCitationStep("closed");
              setSelectedDocForCitation(null);
            }
          }}
          document={selectedDocForCitation.document}
          signedUrl={selectedDocForCitation.signedUrl}
          videoDocument={selectedDocForCitation.videoDocument}
          onCitationCreate={handleVideoCitationCreate}
        />
      )}
    </AttributeTestAnalysisProvider>
  );
};
